import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { ProofLink } from "./Bookkeeping";

export const REQ_ACTIVE = ["MENUNGGU_PENGELOLA", "MENUNGGU_PT_SJI"];
const TONE = {
  MENUNGGU_PENGELOLA: "bg-amber-100 text-amber-900 border-amber-300", MENUNGGU_PT_SJI: "bg-sky-100 text-sky-900 border-sky-300",
  DISETUJUI: "bg-emerald-100 text-emerald-800 border-emerald-300", DITOLAK_PENGELOLA: "bg-red-100 text-red-800 border-red-300",
  DITOLAK_PT_SJI: "bg-red-100 text-red-800 border-red-300", DIBATALKAN: "bg-slate-100 text-slate-600 border-slate-300",
  KEDALUWARSA: "bg-slate-100 text-slate-600 border-slate-300",
};
const ACTION = { DIAJUKAN: "Diajukan", DITERIMA_PENGELOLA: "Diterima Pengelola", DITOLAK_PENGELOLA: "Ditolak Pengelola",
  DISETUJUI: "Disetujui PT SJI", DITOLAK_PT_SJI: "Ditolak PT SJI", DIBATALKAN: "Dibatalkan", KEDALUWARSA: "Dibatalkan otomatis", DIBATALKAN_OTOMATIS: "Dibatalkan otomatis" };
export const Deadline = ({ r }) => (r.status === "MENUNGGU_PENGELOLA" && r.respondBy
  ? <p className="text-xs text-amber-800 mt-1" data-testid={`req-deadline-${r.id}`}>Batas jawaban Pengelola: {wibTime(r.respondBy)} — dibatalkan otomatis bila tidak dijawab.</p> : null);

export const MyNotifications = ({ testid = "my-notifications" }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/my-notifications").then((r) => {
      setD(r.data);
      if (r.data.unread) api.post("/my-notifications/read-all").then(() => window.dispatchEvent(new Event("manager-inbox-updated")));
    }).catch(() => {});
  }, []);
  if (!d?.items.length) return null;
  return (
    <ul className="rounded-xl border bg-white p-4 space-y-1 text-xs" data-testid={testid}>
      <li className="text-sm font-bold text-slate-800 mb-1">Notifikasi</li>
      {d.items.slice(0, 10).map((n) => <li key={n.id} className={n.read ? "text-slate-500" : "font-semibold text-slate-900"} data-testid={`${testid}-${n.id}`}>{wibTime(n.at)} · {n.text} <ProofLink n={n} testid={`${testid}-proof-${n.id}`} /></li>)}
    </ul>
  );
};
export const wibTime = (iso) => new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" }) + " WIB";

export const ReqBadge = ({ r, testid }) => (
  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold ${TONE[r.status]}`} data-testid={testid}>{r.statusLabel}</span>);

export const ReqHistory = ({ r }) => (
  <ol className="mt-2 space-y-0.5 text-[11px] text-slate-600" data-testid={`req-history-${r.id}`}>
    {r.history.map((h) => <li key={h.at + h.action}>{wibTime(h.at)} · {ACTION[h.action] || h.action} oleh {h.byName}{h.note ? ` — “${h.note}”` : ""}</li>)}
  </ol>);

export function useProjectRequests(projectId) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { if (projectId) { api.get(`/projects/${projectId}/manager-requests`).then((r) => setRows(r.data)).catch(() => setRows([])); } }, [projectId]);
  useEffect(() => { load(); }, [load]);
  return { rows, active: rows.find((r) => REQ_ACTIVE.includes(r.status)), reload: load };
}

export const InvestorNotifications = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/my-notifications").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const markRead = (n) => {
    if (n.read) return;
    api.post(`/my-notifications/${n.id}/read`).then(() => { load(); window.dispatchEvent(new Event("manager-inbox-updated")); }).catch(() => {});
  };
  if (!d) return null;
  return (
    <section className="mb-6 rounded-xl border bg-white p-4 sm:p-5" data-testid="investor-dash-notifications">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-bold text-slate-900"><Bell className="h-4 w-4 text-emerald-800" />Notifikasi Terbaru
          {d.unread > 0 && <span className="rounded-full bg-amber-400 px-2 text-[11px] font-bold text-emerald-950" data-testid="investor-dash-notif-unread">{d.unread} baru</span>}</p>
        <Link to="/dashboard/investor/pengelola" className="text-xs font-semibold text-emerald-800 underline" data-testid="investor-dash-notif-all">Lihat semua</Link>
      </div>
      {!d.items.length ? <p className="text-sm text-slate-500" data-testid="investor-dash-notif-empty">Belum ada notifikasi.</p> : (
        <ul className="divide-y">{d.items.slice(0, 5).map((n) => (
          <li key={n.id} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-testid={`investor-dash-notif-${n.id}`}>
            <div className="min-w-0"><p className={`text-sm break-words ${n.read ? "text-slate-600" : "font-semibold text-slate-900"}`}>{!n.read && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" />}{n.text}</p>
              <p className="text-xs text-slate-500">{wibTime(n.at)}</p></div>
            <div className="flex shrink-0 gap-3 text-xs font-semibold">
              <ProofLink n={n} testid={`investor-dash-notif-proof-${n.id}`} />
              {n.activityId && <Link to={`/dashboard/investor/kegiatan/${n.activityId}`} onClick={() => markRead(n)} className="text-emerald-800 underline" data-testid={`investor-dash-notif-activity-${n.id}`}>Lihat kegiatan</Link>}
              {n.projectId && <Link to={`/dashboard/investor/proyek/${n.projectId}`} onClick={() => markRead(n)} className="text-emerald-800 underline" data-testid={`investor-dash-notif-project-${n.id}`}>Buka kebun</Link>}
              {!n.read && <button type="button" onClick={() => markRead(n)} className="text-slate-500 underline" data-testid={`investor-dash-notif-read-${n.id}`}>Tandai dibaca</button>}
            </div>
          </li>))}</ul>)}
    </section>
  );
};

export const ProjectRequestPanel = ({ rows, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const cancel = async (r) => {
    if (!window.confirm("Batalkan pengajuan pengelola ini?")) return;
    setBusy(true);
    try { await api.post(`/manager-requests/${r.id}/cancel`); toast.success("Pengajuan dibatalkan"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="project-manager-requests">
      <p className="text-sm font-bold text-slate-800">Status Pengajuan Pengelola</p>
      <p className="text-xs text-slate-500">Alur: diajukan → Pengelola menerima → PT SJI menyetujui → resmi ditetapkan. Pengelola lama tetap bertugas sampai disetujui.</p>
      {(all ? rows : rows.slice(0, 5)).map((r) => (
        <div key={r.id} className="border-t pt-3 text-sm" data-testid={`project-req-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p><b>{r.managerName}</b> <span className="text-xs text-slate-500">· {r.managerType === "FARMER_GROUP" ? "Kelompok Tani/Koperasi" : "Pengelola Perorangan"}</span></p>
            <div className="flex items-center gap-2"><ReqBadge r={r} testid={`project-req-status-${r.id}`} />
              {REQ_ACTIVE.includes(r.status) && <Button size="sm" variant="outline" disabled={busy} onClick={() => cancel(r)} data-testid={`project-req-cancel-${r.id}`}>Batalkan</Button>}</div>
          </div>
          <Deadline r={r} />
          <ReqHistory r={r} />
        </div>))}
      {rows.length > 5 && <button type="button" className="text-xs text-emerald-800 underline" onClick={() => setAll(!all)} data-testid="project-req-toggle-all">{all ? "Tampilkan lebih sedikit" : `Tampilkan semua (${rows.length})`}</button>}
    </div>
  );
};
